import { memo, useState } from "react";
import BestStartButton from "../../../components/button";
import {
  IoAddCircleOutline,
  IoArrowDown,
  IoArrowUp,
  IoChevronDown,
  IoChevronUp,
  IoCreateOutline,
  IoEyeOutline,
  IoFlagOutline,
  IoInformationCircleOutline,
  IoRemoveOutline,
} from "react-icons/io5";
import {
  humanizeLabel,
  isMissingValue,
  getValuePresentation,
  getBandStatus,
  getBudgetIndicator,
  getGroupStatus,
  formatValue,
  getInputMode,
  getPlaceholder,
  isValidInputForFormat,
  getFormatSuffix,
  decimalToDisplayPercent,
  displayPercentToDecimal,
} from "../helpers";

function ScorecardItemInput({ item, isMissing, valuePresentation, onUpdateValue }) {
  const formatting = item.formatting ?? "";
  const isPercent = formatting.includes("%");
  const [localValue, setLocalValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const displayValue = isEditing
    ? localValue
    : isPercent
      ? decimalToDisplayPercent(item.value)
      : (item.value ?? "");

  const handleFocus = () => {
    setIsEditing(true);
    setLocalValue(
      isPercent ? decimalToDisplayPercent(item.value) : (item.value ?? "")
    );
  };

  const handleChange = (event) => {
    const raw = event.target.value;
    if (!isValidInputForFormat(raw, formatting)) return;

    setLocalValue(raw);

    if (isPercent) {
      if (raw === "" || raw === "-") {
        onUpdateValue(raw);
      } else if (!raw.endsWith(".")) {
        onUpdateValue(displayPercentToDecimal(raw));
      }
    } else {
      onUpdateValue(raw);
    }
  };

  const handleBlur = () => {
    if (isPercent && localValue !== "" && localValue !== "-") {
      onUpdateValue(displayPercentToDecimal(localValue));
    }
    setIsEditing(false);
  };

  const suffix = getFormatSuffix(formatting);
  const showSuffix = suffix && displayValue != null && displayValue !== "";

  return (
    <div className="scorecard-input-wrapper">
      <input
        className={`scorecard-item-input ${isMissing ? "missing" : ""}`}
        type="text"
        inputMode={getInputMode(item.type)}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={getPlaceholder(item)}
        style={valuePresentation}
      />
      {showSuffix && (
        <span
          className="scorecard-input-suffix"
          style={{ left: `calc(11px + ${String(displayValue).length}ch)` }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

function ScorecardFormGrid({
  groups,
  isViewingHistory,
  hasPreviousMonth,
  currentGoals,
  previousMonthGoals,
  findGoal,
  onUpdateItemValue,
  onOpenGoalDialog,
  onOpenPreviousMonthDialog,
}) {
  const [expandedGroup, setExpandedGroup] = useState(null);

  const toggleGroup = (groupName) => {
    setExpandedGroup((prev) => prev === groupName ? null : groupName);
  };

  return (
    <div className="scorecard-form-grid">
      {groups.map((group, groupIndex) => {
        const groupItems = group.items ?? [];
        const groupMissingCount = groupItems.filter((item) => isMissingValue(item.value)).length;
        const isCollapsed = expandedGroup !== group.groupName;

        const groupBandCounts = { passed: 0, warning: 0, failed: 0 };
        const groupBudgetCounts = { underBudget: 0, overBudget: 0 };
        for (const item of groupItems) {
          const status = getBandStatus(item);
          if (status) {
            groupBandCounts[status]++;
          }
          const budget = getBudgetIndicator(item);
          if (budget) {
            if (budget.status === "on-track") groupBudgetCounts.underBudget++;
            else if (budget.status === "off-track") groupBudgetCounts.overBudget++;
          }
        }

        const currentFocusCount = (currentGoals ?? []).filter(
          (g) => g.kpiFocusGroupName === group.groupName
        ).length;
        const previousFocusCount = (previousMonthGoals ?? []).filter(
          (g) => g.kpiFocusGroupName === group.groupName
        ).length;

        const groupStatus = getGroupStatus(groupBandCounts, groupMissingCount);

        return (
          <article className={`scorecard-group-card group-status-${groupStatus}`} key={group.groupName}>
            <button
              type="button"
              className="scorecard-group-header scorecard-group-toggle"
              onClick={() => toggleGroup(group.groupName)}
              aria-expanded={!isCollapsed}
              aria-controls={`group-items-${groupIndex}`}
            >
              <div className="scorecard-group-title-area">
                <span className={`group-status-dot ${groupStatus}`} aria-label={`Group status: ${groupStatus}`} />
                <div>
                  <span className="home-operations-label">KPI group</span>
                  <h4>{humanizeLabel(group.groupName)}</h4>
                </div>
              </div>
              <div className="scorecard-group-header-right">
                <div className="scorecard-group-band-summary">
                  {groupBandCounts.passed > 0 && <span className="band-badge passed">{groupBandCounts.passed} Acheiving</span>}
                  {groupBandCounts.warning > 0 && <span className="band-badge warning">{groupBandCounts.warning} Review</span>}
                  {groupBandCounts.failed > 0 && <span className="band-badge failed">{groupBandCounts.failed} Needs attention</span>}
                  {groupMissingCount > 0 && <span className="band-badge missing">{groupMissingCount} Missing</span>}
                  {groupBudgetCounts.underBudget > 0 && <span className="budget-badge under-budget">{groupBudgetCounts.underBudget} Under Budget</span>}
                  {groupBudgetCounts.overBudget > 0 && <span className="budget-badge over-budget">{groupBudgetCounts.overBudget} Over Budget</span>}
                </div>
                <div className="scorecard-group-focus-summary">
                 { currentFocusCount > 0 && <span className="focus-badge current">{currentFocusCount} {`focus${currentFocusCount > 1 ? "es" : ""}`}</span> }
                  { previousFocusCount > 0 && <span className="focus-badge previous">{previousFocusCount} Prev month</span> }
                </div>
                <span className="scorecard-group-chevron" aria-hidden="true">
                  {isCollapsed ? <IoChevronDown size={18} /> : <IoChevronUp size={18} />}
                </span>
              </div>
            </button>

            {!isCollapsed && (
              <div className="scorecard-item-list" id={`group-items-${groupIndex}`}>
                {groupItems.map((item, itemIndex) => {
                const isMissing = isMissingValue(item.value);
                const bandStatus = getBandStatus(item);
                const valuePresentation = getValuePresentation(item);
                const formattedValue = formatValue(item.value, item);
                const budgetIndicator = getBudgetIndicator(item);
                const formattedBudget = item.budgetedValue != null ? formatValue(item.budgetedValue, item) : null;
                const existingGoal = findGoal(group.groupName, item.name);
                const hasPreviousMonthFocus = (previousMonthGoals ?? []).some(
                  (g) => g.kpiFocusGroupName === group.groupName && g.kpiFocusFieldName === item.name
                );
                const canOpenPreviousMonth = hasPreviousMonth && hasPreviousMonthFocus;
                const showGoalAction = !isViewingHistory || Boolean(existingGoal);
                const goalActionLabel = isViewingHistory
                  ? "View focus"
                  : (existingGoal ? "Edit focus" : "Add focus");

                return (
                  <div className={`scorecard-item-card ${isMissing ? "missing" : "ready"}`} key={`${group.groupName}-${item.displayName}`}>
                    <div className="scorecard-item-copy">
                      <div className="scorecard-item-heading-row">
                        <span className="scorecard-item-name">{item.displayName}</span>
                        {bandStatus && (
                          <span
                            className={`scorecard-item-dot ${bandStatus === "passed" ? "success" : bandStatus}`}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      {item.fieldNotes && (
                        <span className="scorecard-item-info-icon" data-tooltip={item.fieldNotes}>
                          <IoInformationCircleOutline aria-label={item.fieldNotes} />
                        </span>
                      )}
                    </div>

                    {!isViewingHistory && item.allowEdit ? (
                      <ScorecardItemInput
                        item={item}
                        isMissing={isMissing}
                        valuePresentation={valuePresentation}
                        onUpdateValue={(newValue) => onUpdateItemValue(groupIndex, itemIndex, newValue)}
                      />
                    ) : (
                      <div className="scorecard-item-value" style={valuePresentation}>
                        <span className="scorecard-item-value-text">{formattedValue}</span>
                      </div>
                    )}

                    {budgetIndicator && (
                      <div className={`scorecard-budget-indicator ${budgetIndicator.status}`}>
                        {budgetIndicator.direction === "above" && <IoArrowUp aria-hidden="true" size={12} />}
                        {budgetIndicator.direction === "below" && <IoArrowDown aria-hidden="true" size={12} />}
                        {budgetIndicator.direction === "equal" && <IoRemoveOutline aria-hidden="true" size={12} />}
                        <span>Budget: {formattedBudget}</span>
                      </div>
                    )}

                    <div className="scorecard-item-focus-row">
                      <div className="scorecard-item-focus-copy">
                        <span className={`scorecard-item-focus-badge ${existingGoal ? "saved" : "empty"}`}>
                          <IoFlagOutline aria-hidden="true" />
                          <span>{existingGoal ? "Focus saved" : "No focus note"}</span>
                        </span>
                      </div>

                      <div className="scorecard-item-actions">
                        {canOpenPreviousMonth && (
                          <BestStartButton
                            aria-label="View last month entry"
                            className="scorecard-item-focus-action"
                            color="green"
                            content={<IoEyeOutline aria-hidden="true" size={16} />}
                            height={32}
                            onClick={() => onOpenPreviousMonthDialog(group, item)}
                            title="View last month entry"
                            width={32}
                          />
                        )}
                        {showGoalAction && (
                          <BestStartButton
                            aria-label={goalActionLabel}
                            className="scorecard-item-focus-action"
                            color="blue"
                            content={existingGoal ? <IoCreateOutline aria-hidden="true" size={16} /> : <IoAddCircleOutline aria-hidden="true" size={16} />}
                            height={32}
                            onClick={() => onOpenGoalDialog(group, item)}
                            title={goalActionLabel}
                            width={32}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

export default memo(ScorecardFormGrid);
